// Enlarges a clicked photo in a modal overlay, with prev/next navigation
// between photos that share the same data-lightbox-group (e.g. a review's
// before/after pair) — swipe, arrow keys, and on-screen buttons all step
// through the same group. Closes via the X button, clicking the backdrop,
// or Esc, mirroring the Compare/Slideshow modal pattern used in the
// customer account recovery journey extension.
class PhotoLightbox extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-lightbox]');
    if (!this.modal) return;

    this.image = this.modal.querySelector('[data-lightbox-image]');
    this.caption = this.modal.querySelector('[data-lightbox-caption]');
    this.prevButton = this.modal.querySelector('[data-lightbox-prev]');
    this.nextButton = this.modal.querySelector('[data-lightbox-next]');

    this.groups = new Map();
    this.activeGroup = null;
    this.activeIndex = 0;

    Array.from(this.querySelectorAll('[data-lightbox-trigger]')).forEach((trigger) => {
      const group = trigger.dataset.lightboxGroup || 'default';
      if (!this.groups.has(group)) this.groups.set(group, []);
      this.groups.get(group).push(trigger);
      trigger.addEventListener('click', () => {
        this.open(group, this.groups.get(group).indexOf(trigger));
      });
    });

    this.modal.querySelectorAll('[data-lightbox-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    this.boundKeydown = (event) => this.handleKeydown(event);

    this.touchStartX = null;
    this.modal.addEventListener(
      'touchstart',
      (event) => {
        this.touchStartX = event.touches[0].clientX;
      },
      { passive: true },
    );
    this.modal.addEventListener('touchend', (event) => {
      if (this.touchStartX === null) return;
      const deltaX = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(deltaX) > 40) this.step(deltaX < 0 ? 1 : -1);
      this.touchStartX = null;
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundKeydown);
  }

  open(group, index) {
    this.activeGroup = group;
    this.activeIndex = index;
    this.render();
    this.modal.hidden = false;
    document.addEventListener('keydown', this.boundKeydown);
  }

  close() {
    this.modal.hidden = true;
    document.removeEventListener('keydown', this.boundKeydown);
  }

  step(delta) {
    const items = this.groups.get(this.activeGroup) || [];
    if (items.length === 0) return;
    this.activeIndex = (this.activeIndex + delta + items.length) % items.length;
    this.render();
  }

  render() {
    const items = this.groups.get(this.activeGroup) || [];
    const trigger = items[this.activeIndex];
    if (!trigger || !this.image) return;

    this.image.src = trigger.dataset.lightboxSrc || '';
    this.image.alt = trigger.dataset.lightboxLabel || '';
    if (this.caption) this.caption.textContent = trigger.dataset.lightboxLabel || '';

    const multiple = items.length > 1;
    if (this.prevButton) this.prevButton.hidden = !multiple;
    if (this.nextButton) this.nextButton.hidden = !multiple;
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowLeft') this.step(-1);
    if (event.key === 'ArrowRight') this.step(1);
  }
}

if (!customElements.get('photo-lightbox')) {
  customElements.define('photo-lightbox', PhotoLightbox);
}
